import React, { useEffect, useState } from 'react';
import styles from './RegistrationButtons.module.css';
import EmailAuthModal from './EmailAuthModal';
import { fetchData } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../context/AppConfigContext';
import { handleError } from '../../context/errors/errorUtils';

import imgTelegram from '../../assets/images/icons/telegram.svg';
import imgEmail from '../../assets/images/icons/email.svg';
import imgGoogleplay from '../../assets/images/icons/googleplay.svg';

const RegistrationButtons = ({ userId, activeScreen }) => {
  const { t, isBrowser, telegrambot, playStoreUrl } = useAppConfig();
  const { initAuth, userInfo, isGuest: temp } = useAuth();

  const [highlighted, setHighlighted] = useState(0);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [message, setMessage] = useState('');
  const [isInstalledPwa, setIsInstalledPwa] = useState(false);

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const referer_id = userInfo.refererId || '';

  const shouldShow = temp === true && activeScreen === 'home';
  const showOnlyEmail = isInstalledPwa;
  const showTelegram = false && !showOnlyEmail;
  const showEmail = true;
  const showGooglePlay = !showOnlyEmail && !isIOS && temp === true;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaStandalone = window.matchMedia('(display-mode: standalone)');
    const mediaFullscreen = window.matchMedia('(display-mode: fullscreen)');
    const mediaMinimalUi = window.matchMedia('(display-mode: minimal-ui)');

    const updatePwaState = () => {
      const iOSStandalone = Boolean(window.navigator.standalone);
      const isInstalled = iOSStandalone || mediaStandalone.matches || mediaFullscreen.matches || mediaMinimalUi.matches;
      setIsInstalledPwa(isInstalled);
    };

    updatePwaState();

    mediaStandalone.addEventListener?.('change', updatePwaState);
    mediaFullscreen.addEventListener?.('change', updatePwaState);
    mediaMinimalUi.addEventListener?.('change', updatePwaState);

    return () => {
      mediaStandalone.removeEventListener?.('change', updatePwaState);
      mediaFullscreen.removeEventListener?.('change', updatePwaState);
      mediaMinimalUi.removeEventListener?.('change', updatePwaState);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setHighlighted((prev) => (prev + 1) % 3);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const handleEmailClick = () => {
    setShowEmailModal(true);
    setMessage('');
  };

  const handleSendMagicLink = async (email) => {
    try {
      await fetchData('/api/auth/magic-link', 'POST', {
        userId,
        referer_id,
        email,
      });
    } catch (err) {
      handleError({
        code: 'UNEXPECTED_AUTH_ERROR',
        error: err,
        context: { module: 'auth', email },
        showToUser: true,
      });
    }
  };

  const sendIntent = async (intent, payload = {}) => {
    try {
      await fetchData('/api/temp/intent', 'POST', {
        userId,
        intent,
        payload,
      });
    } catch (_) {
      // Analytics failure should not break UX.
    }
  };

  if (!shouldShow) return null;

  return (
    <div className={styles.registrationButtonsContainer}>
      {showGooglePlay && (
        <button
          className={`${styles.circleButton} ${highlighted === 2 ? styles.highlighted : ''}`}
          onClick={async () => {
            await sendIntent('googleplay', { from: activeScreen });
            window.open(playStoreUrl, '_blank');
          }}
        >
          <img src={imgGoogleplay} alt="Google Play" />
        </button>
      )}

      {showEmail && (
        <button
          className={`${styles.circleButton} ${highlighted === 1 ? styles.highlighted : ''}`}
          onClick={handleEmailClick}
        >
          <img src={imgEmail} alt="Email" />
        </button>
      )}

      {showEmailModal && (
        <EmailAuthModal
          onClose={() => setShowEmailModal(false)}
          onSendMagicLink={handleSendMagicLink}
        />
      )}

      {showTelegram && (
        <button
          className={`${styles.circleButton} ${highlighted === 0 ? styles.highlighted : ''}`}
          onClick={async () => {
            await sendIntent('telegram', { from: activeScreen, referer_id });
            window.open(`https://t.me/${telegrambot}/start=invite-${referer_id}`, '_blank');
          }}
        >
          <img src={imgTelegram} alt="Telegram" />
        </button>
      )}

      {message && <div className={styles.message}>{message}</div>}
    </div>
  );
};

export default RegistrationButtons;
